import { ScrollingModule } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { CountryCode, getExampleNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { BehaviorSubject, combineLatest, debounceTime, filter, map, startWith, tap } from 'rxjs';

import { InputMaskDirective } from '../../../sdk/forms/mask';
import { FlagIconComponent } from '../../flag-icon';
import { PhoneInputOption } from './phone-input.models';
import { phoneNumberValidator } from './phone-input.validators';
import { PHONE_INPUT_OPTIONS } from './phone-input-options.utils';

const DEFAULT_PHONE_EXAMPLE = '123-456-789';

@Component({
  selector: 'rms-phone-input',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    ScrollingModule,
    FlagIconComponent,
    MatMenuModule,
    MatIcon,
    MatPrefix,
    AsyncPipe,
    TranslateModule,
    InputMaskDirective,
  ],
  templateUrl: './phone-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  @Input() public set defaultCountryCode(code: string | null) {
    this._defaultCountryCode = code;
    this.applyCountryFilters();
  }

  @Input() public set allowedCountries(codes: string[] | null) {
    this._allowedCountries = codes;
    this.applyCountryFilters();
  }

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = inject(DestroyRef);

  public form = this.fb.group({
    countryInput: this.fb.control<string>(''),
    country: this.fb.control<CountryCode | null>(null),
    countryCallingCode: this.fb.control<string>(''),
    nationalNumberExample: this.fb.control<string>(DEFAULT_PHONE_EXAMPLE),
    nationalNumber: this.fb.control<string>(''),
  });

  public options$ = new BehaviorSubject<PhoneInputOption[]>(PHONE_INPUT_OPTIONS);

  public filteredOptions$ = combineLatest([
    this.form.controls.countryInput.valueChanges.pipe(
      startWith(this.form.value.countryInput),
      debounceTime(300),
      map((input) => (input ? input.toLowerCase() : ''))
    ),
    this.options$,
  ]).pipe(
    map(([input, options]) =>
      input.length
        ? options.filter(
            (option) => option.name.toLowerCase().includes(input) || option.countryCallingCode.includes(input)
          )
        : options
    )
  );

  public DEFAULT_PHONE_EXAMPLE = DEFAULT_PHONE_EXAMPLE;

  private _defaultCountryCode: string | null = null;
  private _allowedCountries: string[] | null = null;

  private onChange?: (phone: string) => void;
  private onTouched?: () => void;
  private writingValue = false;

  ngOnInit() {
    this.form.controls.country.valueChanges
      .pipe(
        filter(Boolean),
        map((code) => getExampleNumber(code, examples)),
        tap((phone) =>
          this.form.controls.nationalNumberExample.patchValue(phone ? phone.nationalNumber : DEFAULT_PHONE_EXAMPLE)
        ),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();

    combineLatest([
      this.form.controls.nationalNumber.valueChanges.pipe(startWith(this.form.value.nationalNumber)),
      this.form.controls.country.valueChanges.pipe(startWith(this.form.value.country)),
    ])
      .pipe(
        map(([phoneNumber, country]) => this.onChangeMask(phoneNumber, country)),
        filter(() => !this.writingValue),
        tap((output) => {
          this.onTouched?.();
          this.onChange?.(output);
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  writeValue(phone: string | null): void {
    // Emitted, so the country placeholder and the change stream stay in step, but not reported back to the host control
    this.writingValue = true;

    try {
      this.patchFromValue(phone);
    } finally {
      this.writingValue = false;
    }
  }

  public validate(): ValidationErrors | null {
    // ng-validate calls validate fn before form api validation is updated
    // so we need to run the validator manually here
    return phoneNumberValidator()(this.form);
  }

  public registerOnChange(fn: typeof this.onChange): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: typeof this.onTouched): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.enabled && this.form.disable() : this.form.disabled && this.form.enable();
  }

  public phoneMask = (value: string): string => value.replace(/\D/g, '').slice(0, 15);

  protected onCountryChange(option: PhoneInputOption | undefined): void {
    if (!option) {
      return;
    }

    this.form.patchValue({
      countryInput: '',
      country: option.code,
      countryCallingCode: option.countryCallingCode,
    });
  }

  protected onChangeMask(phoneNumber?: string | null, country?: CountryCode | null): string {
    if (!phoneNumber?.length || !country) {
      return '';
    }

    const parsed = parsePhoneNumberFromString(phoneNumber, country);

    if (!parsed) {
      return '';
    }

    return parsed.format('E.164');
  }

  private patchFromValue(phone: string | null): void {
    const phoneNumber = phone ? parsePhoneNumberFromString(phone) : undefined;

    if (!phoneNumber) {
      this.form.controls.nationalNumber.setValue('');
      this.resetToFirstOption();

      return;
    }

    this.form.patchValue({
      countryInput: '',
      country: phoneNumber.country,
      countryCallingCode: phoneNumber.countryCallingCode,
      nationalNumber: phoneNumber.nationalNumber,
    });
  }

  private resetToFirstOption(): void {
    const first = this.options$.value[0];

    if (first) {
      this.onCountryChange(first);
    } else {
      this.form.patchValue({
        countryInput: '',
        country: null,
        countryCallingCode: '',
        nationalNumber: '',
        nationalNumberExample: DEFAULT_PHONE_EXAMPLE,
      });
    }
  }

  private applyCountryFilters(): void {
    const options: PhoneInputOption[] = this._allowedCountries
      ? PHONE_INPUT_OPTIONS.filter((o) => this._allowedCountries!.includes(o.code))
      : [...PHONE_INPUT_OPTIONS];

    if (this._defaultCountryCode) {
      const defaultCode = this._defaultCountryCode;
      options.sort((a: PhoneInputOption, b: PhoneInputOption) =>
        a.code === defaultCode ? -1 : b.code === defaultCode ? 1 : 0
      );
    }

    this.options$.next(options);

    const currentCountry = this.form.controls.country.value;

    if (!currentCountry || !options.some((o) => o.code === currentCountry)) {
      this.resetToFirstOption();
    }
  }
}
